import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';

import {ClientMemoryApi} from '../../../api/clientMemoryApi';
import {AUTONOMY_LEVELS} from './MemoryAutonomyControl';
import {AUTONOMY_SAVED_TOAST_ID} from './useMemoryAutonomy';

/** Un solo aviso de error por oferta: cada reintento fallido apilaba otro idéntico (QA 27/09). */
const OFFER_FAILED_TOAST_ID = 'memory-autonomy-offer-failed';
const OFFER_TOAST_MS = 5000;

/**
 * «Acabás de subir la autonomía: hay N hechos esperando tu ok que se pueden aprobar
 * automáticamente. ¿Los apruebo?» (card 8uu3IdNd, punto 4).
 *
 * El knob **no es retroactivo por diseño**: mueve con qué estado nacen los hechos de ahí en
 * adelante y no toca la cola. Ese default está bien —un ajuste que reprocesa en silencio lo que ya
 * decidiste mirar sería peor— pero deja un hueco práctico: la agencia sube la autonomía justamente
 * porque la cola le pesa, y la cola no se mueve. Esto es la retroactividad **a pedido y con
 * confirmación**: el backend dice cuántos entrarían (sólo lee) y aprobar sigue siendo un gesto
 * explícito del usuario, resuelto por el bulk que ya existe.
 *
 * Tres decisiones:
 *
 * 1. **Sólo al SUBIR.** Bajar la autonomía no puede ofrecer aprobar nada — sería exactamente lo
 *    contrario de lo que el usuario acaba de pedir. El orden lo da `AUTONOMY_LEVELS`, que es el eje
 *    del riel: no hay una segunda tabla de «cuál es más permisivo».
 * 2. **Después de guardar, no antes.** Si el PUT falla, el ajuste no cambió y no hay nada que
 *    ofrecer; preguntar igual dejaría al usuario aprobando una tanda por un nivel que no rige.
 * 3. **Los ids los manda el backend y se reusan tal cual.** El usuario acepta un número concreto:
 *    volver a derivar la lista del lado del frontend serían dos definiciones de «lo que entraría»
 *    que pueden no coincidir.
 *
 * Es un hook y no un componente porque las dos superficies del knob aprueban distinto: en el
 * Recorrido va por el store del mazo (que trae optimismo y Deshacer) y en el marco clásico por la
 * API + recarga. Lo que no puede diferir es CUÁNDO se ofrece y CUÁNTOS son.
 */

/** ¿`next` es una posición más autónoma que `current`? El eje es el orden del riel. */
export function raisesAutonomy(current, next) {
    const from = AUTONOMY_LEVELS.indexOf(current);
    const to = AUTONOMY_LEVELS.indexOf(next);
    if (to < 0) return false;
    // Sin nivel previo conocido no se ofrece nada: no se puede afirmar que subió.
    return from >= 0 && to > from;
}

export function useAutonomyRaiseOffer(clientId) {
    const {t} = useTranslation();
    const [offer, setOffer] = useState(null);
    const [busy, setBusy] = useState(false);
    // El cliente VIGENTE, actualizado por efecto en cada cambio. `askAfterChange` compara contra
    // esto y no contra su propio `clientId`: hasta el review del 10/09 escribía el ref y después lo
    // comparaba con la misma variable del closure, o sea que la guarda **nunca podía dar distinto**
    // —una tautología— y no cubría el caso para el que existía (hallazgo C-knob-11).
    const currentClientRef = useRef(clientId);
    // ⚠️ El número de secuencia de la ÚLTIMA pregunta. El riel se vuelve a habilitar en cuanto
    // contesta el PUT —antes de que vuelva este GET—, así que el usuario puede subir, bajar y
    // quedarse en otra posición mientras la previsualización del primer cambio sigue en vuelo: al
    // volver abría «Acabas de cambiar este ajuste a «Casi todo»…» con el riel en otro lugar, y
    // aceptar aprobaba con el criterio que ya no rige (C-knob3-07, review del 27/09).
    const askSeqRef = useRef(0);

    useEffect(() => {
        currentClientRef.current = clientId;
        // Y cambiar de cliente TIRA la oferta. El modal sobrevivía al cambio desde el sidebar con
        // los `fact_ids` del cliente anterior: el backend lo tapa (404 uniforme, revalida ownership
        // de cada id), así que no había fuga cross-client, pero el usuario apretaba «sí» y no se
        // aprobaba nada — indistinguible de que no hubiera nada que mover.
        setOffer(null);
    }, [clientId]);

    const dismiss = useCallback(() => setOffer(null), []);

    /**
     * Se llama DESPUÉS de guardar el nivel nuevo. `previous` es el nivel que regía antes.
     * No devuelve nada: si hay algo que ofrecer, lo publica en `offer`.
     */
    const askAfterChange = useCallback((previous, next) => {
        // El contador se mueve SIEMPRE, incluso cuando el cambio es a la baja y esta función no pide
        // nada: bajar el riel también tiene que invalidar la previsualización que quedó en vuelo.
        askSeqRef.current += 1;
        const seq = askSeqRef.current;
        if (!clientId || !raisesAutonomy(previous, next)) return Promise.resolve();
        const asked = String(clientId);
        return ClientMemoryApi.previewAutonomyOnQueue(clientId, next)
            .then((response) => {
                // Llegó tarde: entre el pedido y la respuesta el usuario movió el riel otra vez.
                if (seq !== askSeqRef.current) return;
                if (String(currentClientRef.current) !== asked) return;
                // La rama de error es `!== 'OK'`: el backend manda el código TIPADO en `status`.
                // Y no se le avisa nada al usuario — esto es una oferta, no una operación que pidió:
                // si no se pudo calcular, la pantalla se comporta como antes de la card.
                if (!response || response.status !== 'OK' || !response.data) return;
                const ids = Array.isArray(response.data.fact_ids) ? response.data.fact_ids : [];
                if (!ids.length) return;
                // Cuántos de la cola NO se ofrecen: sensibles, cambios y lo que ya tocó una
                // persona. Sin nombrarlos, la oferta dice «45» y el «Aprobar todo» de al lado
                // «53», y esa diferencia se lee como que uno de los dos miente (QA 09/09).
                // `queue_total` puede no venir (backend viejo): ahí `rest` es 0 y el modal calla,
                // que es exactamente como se comportaba antes.
                const total = Number(response.data.queue_total);
                const rest = Number.isFinite(total) ? Math.max(0, total - ids.length) : 0;
                // ⚠️ `truncated` dice que CALIFICARON más de los que la tanda resuelve (el techo es
                // `MAX_BULK_FACTS`, 200). Sin ese dato, `rest` se le atribuía entero a «necesitan
                // que los mires» y el modal mentía justo en el caso que motivó la oferta: con 260
                // aprobables, 60 de ellos se anunciaban como pendientes de revisión humana cuando
                // habrían entrado solos. El modal elige otra frase con esto (hallazgo C-knob-13).
                // El «Listo…» del guardado se retira ANTES de abrir el modal: los dos salen del mismo
                // gesto y el toast, que vive por encima de todo, le tapaba la X —y a 390 el título—
                // los 5 segundos que dura (QA-VIS-knob2-04). Lo que dice el toast ya lo dice el modal
                // en su primera frase; sin oferta, el toast se queda donde estaba.
                toast.dismiss(AUTONOMY_SAVED_TOAST_ID);
                setOffer({
                    count: ids.length, factIds: ids, level: next, rest,
                    truncated: response.data.truncated === true,
                });
            })
            .catch(() => {});
    }, [clientId]);

    /**
     * El sí del usuario. `approve` es quien sabe aprobar en esta superficie y recibe los ids.
     *
     * ⚠️ **La oferta se cierra SÓLO si la tanda entró.** Hasta el review del 10/09 se cerraba en un
     * `finally` y el error se comía un `.catch(() => {})` que **no dispara nunca**: las dos
     * superficies aprueban por caminos que RESUELVEN con `false` ante `status !== 'OK'` en vez de
     * rechazar (`ClientFactsStore.bulkReview` hace rollback optimista y devuelve `false`; el panel
     * clásico ahora devuelve el booleano de `isOk`). Así que el usuario confirmaba «aprobar N», la
     * tanda fallaba entera —pasa de verdad: si entre la previsualización y el bulk un hecho se
     * borró o quedó rechazado, el backend contesta 404/400 para TODA la tanda (C-knob-08)— y el
     * modal se iba sin un solo aviso. Los hechos seguían en la cola, que es el dato correcto, pero
     * se leía como «no pasó nada», indistinguible de «no había nada que mover».
     *
     * Ahora falla a la vista y el modal queda abierto, que es el único reintento honesto: los ids
     * ya están calculados y volver a apretar «sí» es un gesto, no un recálculo.
     */
    const accept = useCallback((approve) => {
        if (!offer || busy) return Promise.resolve(false);
        setBusy(true);
        const failed = () => {
            const message = t('memory_autonomy.raise_offer_failed');
            // ⚠️ Con un `toastId` ya activo, react-toastify 11 descarta el aviso EN SILENCIO. El modal
            // queda abierto a propósito para reintentar, así que el segundo fallo tiene que renovar el
            // que está en pantalla y no apilar un tercero idéntico.
            if (toast.isActive(OFFER_FAILED_TOAST_ID)) {
                toast.update(OFFER_FAILED_TOAST_ID, {render: message, autoClose: OFFER_TOAST_MS});
            } else {
                toast.error(message, {toastId: OFFER_FAILED_TOAST_ID, autoClose: OFFER_TOAST_MS});
            }
            return false;
        };
        return Promise.resolve(approve(offer.factIds))
            // `undefined` cuenta como éxito: no toda superficie devuelve un booleano, y sólo el
            // `false` explícito es la señal de fallo que estas dos emiten.
            .then((ok) => {
                if (ok === false) return failed();
                setOffer(null);
                return true;
            })
            .catch(failed)
            .finally(() => setBusy(false));
    }, [offer, busy, t]);

    return {offer, busy, askAfterChange, accept, dismiss};
}

export default useAutonomyRaiseOffer;
